import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Search, Loader2, ShoppingCart, Globe } from 'lucide-react';
import { CURRENCIES } from './constants';

// Quotation Header Form
function QuotationHeaderForm({ customers, customerSearch, setCustomerSearch, selectedCustomer, setSelectedCustomer, headerData, setHeaderData, onSubmit, saving, onCancel, customerLocked = false, submitLabel = 'Continue to Add Items' }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Customer Selection */}
      <Card className="card-dashboard">
        <CardHeader>
          <CardTitle className="text-lg font-heading uppercase">Select Customer *</CardTitle>
        </CardHeader>
        <CardContent>
          {!selectedCustomer ? (
            <>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search customers..."
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              {customers.length > 0 && (
                <div className="mt-2 border rounded-lg max-h-60 overflow-y-auto">
                  {customers.map((c) => (
                    <div
                      key={c.id}
                      className="p-3 hover:bg-muted/50 cursor-pointer border-b last:border-0"
                      onClick={() => setSelectedCustomer(c)}
                    >
                      <div className="flex justify-between">
                        <span className="font-medium">{c.name}</span>
                        <Badge variant="outline">{c.customer_code}</Badge>
                      </div>
                      <div className="flex gap-2 mt-1">
                        <Badge className={c.customer_type === 'export' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}>
                          {c.customer_type}
                        </Badge>
                        <span className="text-sm text-muted-foreground">{c.currency || 'INR'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-bold text-lg">{selectedCustomer.name}</p>
                  <p className="text-sm text-muted-foreground">{selectedCustomer.customer_code}</p>
                  <div className="flex gap-2 mt-2">
                    <Badge className={selectedCustomer.customer_type === 'export' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}>
                      {selectedCustomer.customer_type === 'export' ? <><Globe className="h-3 w-3 mr-1" />Export</> : 'Domestic'}
                    </Badge>
                    <Badge variant="outline">{selectedCustomer.country || 'India'}</Badge>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedCustomer(null)} disabled={customerLocked} data-testid="change-customer-button">
                  {customerLocked ? 'From inquiry' : 'Change'}
                </Button>
              </div>
            </div>
          )}

          {/* Inquiry Reference Section */}
          <div className="mt-4 pt-4 border-t space-y-4">
            <p className="text-sm font-medium text-muted-foreground">Inquiry Reference (Optional)</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Inquiry Number</Label>
                <Input
                  value={headerData.inquiry_number}
                  onChange={(e) => setHeaderData(p => ({ ...p, inquiry_number: e.target.value }))}
                  placeholder="e.g., INQ-2026-001"
                />
              </div>
              <div className="space-y-2">
                <Label>Inquiry Date</Label>
                <Input
                  type="date"
                  value={headerData.inquiry_date}
                  onChange={(e) => setHeaderData(p => ({ ...p, inquiry_date: e.target.value }))}
                />
              </div>
            </div>
          </div>

          {/* Contact Person (Editable) */}
          {selectedCustomer && (
            <div className="mt-4 pt-4 border-t space-y-2">
              <Label>Contact Name for this Quotation</Label>
              <Input
                value={headerData.contact_person}
                onChange={(e) => setHeaderData(p => ({ ...p, contact_person: e.target.value }))}
                placeholder="e.g., Mr. John Smith"
                data-testid="contact-person-input"
              />
              <p className="text-xs text-muted-foreground">
                Auto-filled from customer. Edit to use a different contact for this quotation only.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Commercial Terms */}
      <Card className="card-dashboard">
        <CardHeader>
          <CardTitle className="text-lg font-heading uppercase">Commercial Terms</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Currency *</Label>
              <Select value={headerData.currency} onValueChange={(v) => setHeaderData(p => ({ ...p, currency: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Exchange Rate {headerData.currency !== 'INR' && '(INR → ' + headerData.currency + ')'}</Label>
              <Input
                type="number"
                step="0.01"
                value={headerData.exchange_rate}
                onChange={(e) => setHeaderData(p => ({ ...p, exchange_rate: parseFloat(e.target.value) || 1 }))}
                placeholder="e.g., 84.50"
              />
              {headerData.currency !== 'INR' && (
                <p className="text-xs text-muted-foreground">₹100 = {headerData.currency} {(100 / headerData.exchange_rate).toFixed(2)}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Handling Charge ({headerData.currency})</Label>
              <Input
                type="number"
                value={headerData.handling_charge}
                onChange={(e) => setHeaderData(p => ({ ...p, handling_charge: parseFloat(e.target.value) || 0 }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Validity (Days)</Label>
              <Input
                type="number"
                value={headerData.validity_days}
                onChange={(e) => setHeaderData(p => ({ ...p, validity_days: parseInt(e.target.value) || 30 }))}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Tax %</Label>
              <Input
                type="number"
                value={headerData.tax_percentage}
                onChange={(e) => setHeaderData(p => ({ ...p, tax_percentage: parseFloat(e.target.value) || 0 }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Discount %</Label>
              <Input
                type="number"
                value={headerData.discount_percentage}
                onChange={(e) => setHeaderData(p => ({ ...p, discount_percentage: parseFloat(e.target.value) || 0 }))}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Payment Terms</Label>
            <Input
              value={headerData.payment_terms}
              onChange={(e) => setHeaderData(p => ({ ...p, payment_terms: e.target.value }))}
              placeholder="e.g., 30% advance, 70% on delivery"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Delivery Terms</Label>
              <Input
                value={headerData.delivery_terms}
                onChange={(e) => setHeaderData(p => ({ ...p, delivery_terms: e.target.value }))}
                placeholder="e.g., Ex-Works"
              />
            </div>
            <div className="space-y-2">
              <Label>Incoterms</Label>
              <Select value={headerData.incoterms} onValueChange={(v) => setHeaderData(p => ({ ...p, incoterms: v }))}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="EXW">EXW - Ex Works</SelectItem>
                  <SelectItem value="FOB">FOB - Free on Board</SelectItem>
                  <SelectItem value="CIF">CIF - Cost, Insurance & Freight</SelectItem>
                  <SelectItem value="DDP">DDP - Delivered Duty Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={headerData.notes}
              onChange={(e) => setHeaderData(p => ({ ...p, notes: e.target.value }))}
              placeholder="Internal notes..."
              rows={2}
            />
          </div>

          {/* Terms & Conditions */}
          <div className="space-y-2">
            <Label>Terms & Conditions</Label>
            <Textarea
              value={headerData.terms_conditions}
              onChange={(e) => setHeaderData(p => ({ ...p, terms_conditions: e.target.value }))}
              placeholder="Terms and conditions for this quotation..."
              rows={4}
            />
            <p className="text-xs text-muted-foreground">
              {selectedCustomer?.default_terms ? 'Auto-filled from customer defaults. Edit as needed.' : 'Enter terms for this quotation.'}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="lg:col-span-2 flex justify-end gap-3">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button className="btn-primary" onClick={onSubmit} disabled={saving || !selectedCustomer} data-testid="quotation-header-submit-button">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShoppingCart className="mr-2 h-4 w-4" />}
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}

export default QuotationHeaderForm;
